import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { companyService } from '../services/companyService';

const CompanyContext = createContext();

export { CompanyContext };

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Buscar todas as empresas (apenas para admin)
  const fetchCompanies = async () => {
    if (user?.role !== 'admin') return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await companyService.getAll();
      setCompanies(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  // Buscar dados da empresa atual
  const fetchCurrentCompany = async () => {
    if (!user?.companyId) return;
    
    setLoading(true);
    setError(null);
    try {
      const company = await companyService.getById(user.companyId);
      setCurrentCompany(company);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching current company:', err);
    } finally {
      setLoading(false);
    }
  };

  // Adicionar nova empresa (apenas admin)
  const addCompany = async (companyData) => {
    if (user?.role !== 'admin') throw new Error('Apenas administradores podem criar empresas');
    
    try {
      const newCompany = await companyService.create(companyData);
      setCompanies(prev => [...prev, newCompany]);
      return newCompany;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Atualizar empresa
  const updateCompany = async (companyId, companyData) => {
    if (user?.role !== 'admin') throw new Error('Apenas administradores podem editar empresas');
    
    try {
      const updatedCompany = await companyService.update(companyId, companyData);
      setCompanies(prev => 
        prev.map(company => 
          company.id === companyId ? updatedCompany : company
        )
      );
      
      // Atualizar empresa atual se for a mesma
      if (currentCompany?.id === companyId) {
        setCurrentCompany(updatedCompany);
      }
      
      return updatedCompany;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Deletar empresa
  const deleteCompany = async (companyId) => {
    if (user?.role !== 'admin') throw new Error('Apenas administradores podem deletar empresas');
    
    try {
      await companyService.delete(companyId);
      setCompanies(prev => prev.filter(company => company.id !== companyId));
      
      // Limpar empresa atual se for a mesma
      if (currentCompany?.id === companyId) {
        setCurrentCompany(null);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Buscar empresa por ID
  const getCompanyById = (companyId) => {
    return companies.find(company => company.id === companyId);
  };

  // Buscar dados quando o usuário muda
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchCompanies();
    } else if (user?.companyId) {
      fetchCurrentCompany();
    }
  }, [user?.role, user?.companyId]);

  const value = {
    companies,
    currentCompany,
    loading,
    error,
    addCompany,
    updateCompany,
    deleteCompany,
    getCompanyById,
    refreshCompanies: fetchCompanies,
    refreshCurrentCompany: fetchCurrentCompany
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyProvider;